import React, { useState, useEffect, useCallback, useContext } from "react";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import ConfirmModal from "./ConfirmModal";
import './MyCompany.css';
import { NotificationsContext } from "../hooks/notificationsContext";

export default function CompanyDividendsTab({ companyDoc }) {
  const [dividendPercent, setDividendPercent] = useState(0.01);
  const [newStockPrice, setNewStockPrice] = useState(companyDoc?.companyStockPrice || 0);
  const [fees, setFees] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);

  const { addNotification } = useContext(NotificationsContext);
  const twentyFourHrs = 24 * 60 * 60 * 1000;

  useEffect(() => {
    if (!companyDoc?.companyStockPrice) return;
    const currentPrice = Number(companyDoc.companyStockPrice);
    setNewStockPrice(currentPrice - currentPrice * (dividendPercent / 100));
  }, [dividendPercent, companyDoc]);

  const calculateFees = useCallback(async () => {
    if (!companyDoc) return;
    try {
      const globalSnap = await getDoc(doc(db, "Global_Vars", "Main"));
      if (!globalSnap.exists()) return;
      const globalData = globalSnap.data();
      const baseTaxRate = globalData.globalBaseTaxRate / 100;
      const percent = dividendPercent / 100;
      const price = companyDoc.companyStockPrice;
      const volume = companyDoc.companyStockVolume;
      const outstanding = companyDoc.companyOutstanding || 0;
      const base = price * percent * (volume - outstanding);
      setFees(base * (baseTaxRate / 2));
    } catch (err) { console.error(err); setFees(0); }
  }, [dividendPercent, companyDoc]);

  useEffect(() => { calculateFees(); }, [calculateFees]);

  const calculateCooldown = useCallback(async () => {
    try {
      const user = auth.currentUser;
      if (!user || !companyDoc) return;
      const now = Date.now();
      const companySnap = await getDoc(doc(db, "Companies", companyDoc.id));
      const companyData = companySnap.exists() ? companySnap.data() : {};
      const companyCooldown = Math.max(0, twentyFourHrs - (now - (companyData.companyDividendsIssuedAt || 0)));
      
      const userSnap = await getDoc(doc(db, "Users", user.uid));
      const userData = userSnap.exists() ? userSnap.data() : {};
      const userCooldown = Math.max(0, twentyFourHrs - (now - (userData.userIssuedDividendsAt || 0)));
      
      setCooldownRemaining(Math.max(companyCooldown, userCooldown));
    } catch (err) { setCooldownRemaining(0); }
  }, [companyDoc, twentyFourHrs]);

  useEffect(() => {
    calculateCooldown();
    const interval = setInterval(calculateCooldown, 1000);
    return () => clearInterval(interval);
  }, [calculateCooldown]);

  const formatTime = (ms) => {
    const totalSec = Math.ceil(ms / 1000);
    const h = Math.floor(totalSec / 3600).toString().padStart(2, "0");
    const m = Math.floor((totalSec % 3600) / 60).toString().padStart(2, "0");
    const s = (totalSec % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  const handleConfirmDividends = async () => {
    setShowConfirm(false);
    setLoading(true);
    try {
      const user = auth.currentUser;
      const token = await user.getIdToken(true);
      const res = await fetch("https://issuedividends-5dhfwyjvzq-uc.a.run.app", {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ uid: user.uid, companyTicker: companyDoc.id, percentDividends: Number(dividendPercent) }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to issue dividends");
      addNotification(`Authorized: ${dividendPercent.toFixed(2)}% yield issued.`, "success");
      calculateCooldown();
    } catch (err) { addNotification(err.message, "error"); }
    finally { setLoading(false); }
  };

  return (
    <div className="issue-tab-container">
      {/* Top Stats */}
<div className="issue-stats-grid">
  <div className="stat-card">
    <label>Current Price</label>
    <div className="stat-value">
      ${Number(companyDoc.companyStockPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </div>
  </div>
  
  <div className="stat-card highlight">
    <label>Adjusted Price</label>
    <div className="stat-value">
      ${newStockPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
    </div>
  </div>
</div>

      <div className="issue-main-content">
        <div className="input-section">
          <div className="cool-input-group">
            <label>Dividend Percentage</label>
            <input 
              type="number" 
              step="0.01"
              value={dividendPercent} 
              onChange={(e) => setDividendPercent(Math.min(10, Math.max(0.01, parseFloat(e.target.value) || 0.01)))} 
              disabled={cooldownRemaining > 0 || loading}
            />
            <span className="input-unit">%</span>
          </div>
          
          {/* Visual Slider */}
          <div className="cool-input-group" style={{ marginTop: '-8px' }}>
            <input 
              type="range" 
              min="0.01" 
              max="10.0" 
              step="0.01" 
              value={dividendPercent} 
              onChange={(e) => setDividendPercent(Number(e.target.value))}
              disabled={cooldownRemaining > 0 || loading}
              className="teal-range-slider"
            />
          </div>
        </div>

        {/* Financial Preview */}
        <div className="issue-preview-card">
          <div className="preview-row total">
            <span>TAX:</span>
            <span>-${fees.toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
          </div>
        </div>
      </div>

      <div className="action-area">
        {cooldownRemaining > 0 ? (
          <button className="issue-btn-disabled" disabled>
            COOLDOWN: {formatTime(cooldownRemaining)}
          </button>
        ) : (
          <button 
            className={`issue-btn-active ${loading ? 'loading' : ''}`} 
            onClick={() => setShowConfirm(true)}
            disabled={loading}
          >
            {loading ? "AUTHORIZING..." : `ISSUE ${dividendPercent.toFixed(2)}% DIVIDEND`}
          </button>
        )}
      </div>

      {showConfirm && (
        <ConfirmModal
          title="AUTHORIZE DISBURSEMENT"
          message={`Disburse ${dividendPercent.toFixed(2)}% in dividends? Total fees of $${fees.toFixed(2)}.`}
          confirmText="Confirm"
          cancelText="Cancel"
          onConfirm={handleConfirmDividends}
          onCancel={() => setShowConfirm(false)}
        />
      )}
    </div>
  );
}